/* ----------------------------------------------------------------------------
   M5 shared bits — recon lifecycle metadata, vertical chips, reason codes and
   the dispute-commit mutation. Ports the lifecycle model from the mockup m5.js.

   Dispute lifecycle (the "remark"/status):
     No Dispute · Open — Courier End · Open — Prozo End · CN Pending ·
     Closed & Shared with Finance · Rejected

   RBAC note: this frontend replica runs as a ProShip Admin viewer (sees both D2C
   & PTL, may Close & Share with Finance and Record Payment), so every capability
   in the mockup is exercised.
   ---------------------------------------------------------------------------- */
import React from 'react'
import {TODAY, inr} from '../dummyData'

export const RECON = [
  {k: 'No Dispute', c: '#2E7D32', bg: '#E8F5E9'},
  {k: 'Open — Courier End', c: '#B45309', bg: '#FFF7ED'},
  {k: 'Open — Prozo End', c: '#1565C0', bg: '#E3F2FD'},
  {k: 'CN Pending', c: '#6A1B9A', bg: '#F3E5F5'},
  {k: 'Closed & Shared with Finance', c: '#0F766E', bg: '#CCFBF1'},
  {k: 'Rejected', c: '#C62828', bg: '#FEEBEE'},
]
export const OPEN_STATES = ['Open — Courier End', 'Open — Prozo End', 'CN Pending']
export const FINAL = 'Closed & Shared with Finance'
export const STEPPER_STATES = [
  'Open — Courier End',
  'Open — Prozo End',
  'CN Pending',
  'Closed & Shared with Finance',
]
export const BANKS = [
  'HDFC Bank',
  'ICICI Bank',
  'Axis Bank',
  'State Bank of India',
  'Kotak Mahindra Bank',
  'Yes Bank',
]
export const INV_REASONS = [
  'Weight discrepancy',
  'Zone mismatch',
  'Surcharge dispute',
  'RTO billed as forward',
  'Duplicate billing',
  'Rate-card variance',
  'Fuel surcharge revision',
  'COD fee mismatch',
]

/* the signed-in operator for this replica session */
export const ME = 'ashwani.kumar@prozo.com'

export const reconMeta = (s) => RECON.find((r) => r.k === s) || RECON[0]
export const isDisputed = (i) => i.reconStatus !== 'No Dispute'
export const isOpen = (i) => OPEN_STATES.indexOf(i.reconStatus) >= 0
export const isClosed = (i) => i.reconStatus === FINAL
export const needsCN = (status) => status === FINAL
export const shortStatus = (s) => s.replace('Closed & Shared with Finance', 'Closed → Finance')

/* lifecycle badge — coloured exactly like the mockup reconBadge() */
export function ReconBadge({status}) {
  const m = reconMeta(status)
  return (
    <span className='badge' style={{color: m.c, background: m.bg}} title={status}>
      <span className='dot' style={{background: m.c}} />
      {shortStatus(status)}
    </span>
  )
}

/* vertical chip (D2C / PTL) — chip-d2c / chip-ptl */
export function VertChip({vertical}) {
  return <span className={'chip chip-' + String(vertical).toLowerCase()}>{vertical}</span>
}

/* distinct month / courier helpers over a row set */
export function monthsOf(rows) {
  const seen = {}
  const out = []
  rows.forEach((i) => {
    if (!seen[i.monthKey]) {
      seen[i.monthKey] = 1
      out.push({k: i.monthKey, label: i.period})
    }
  })
  out.sort((a, b) => (a.k < b.k ? -1 : 1))
  return out
}
export function couriersOf(rows) {
  const seen = {}
  const out = []
  rows.forEach((i) => {
    if (!seen[i.partner]) {
      seen[i.partner] = 1
      out.push(i.partner)
    }
  })
  return out.sort()
}

/* mutate an invoice in place to apply a dispute transition (mirrors commit()) */
export function commitDispute(i, chg) {
  i.reason = chg.reason
  i.reconStatus = chg.status
  i.owner = chg.owner
  if (chg.cn != null) i.cnAmount = chg.cn
  if (!i.raisedDate) i.raisedDate = TODAY.toISOString()
  if (chg.status === FINAL || chg.status === 'Rejected') {
    i.resolvedDate = TODAY.toISOString()
    i.resolutionDays = Math.max(
      0,
      Math.round((new Date(i.resolvedDate) - new Date(i.raisedDate)) / 86400000)
    )
    i.status = chg.status === 'Rejected' ? 'Approved' : 'Paid'
  } else {
    i.resolvedDate = null
    i.resolutionDays = null
    i.status = 'Disputed'
  }
  if (i.reconStatus === 'No Dispute') i.disputedAmount = 0
  else if (!i.disputedAmount) i.disputedAmount = i.variance
  i.history = i.history || []
  const note = chg.note || ''
  i.history.push({
    status: chg.status,
    by: ME,
    at: TODAY.toISOString(),
    reason: (note + (chg.status === FINAL ? ' · CN ' + inr(chg.cn) : '')).trim() || chg.status,
  })
}
